import {useCallback, useRef, useState} from "react";
import Header from "./Header";
import Loading from "./Loading";
import {
    fetchMultiplePresignedUrls,
    fetchSinglePresignedUrl, insertMultipleManuscripts,
    uploadManuscriptsBatch
} from "../service/ManuscriptService";
import AlertMessage from "./AlertMessage";
import {useNavigate} from "react-router-dom";

const MAX_FILES = 5;

function UploadBatch() {
    const [dragOver, setDragOver] = useState(false);
    const [files, setFiles] = useState([]); // File[]
    const [batchName, setBatchName] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const inputRef = useRef(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const [email, setEmail] = useState(null);
    const navigate = useNavigate();

    const isFull = files.length >= MAX_FILES;

    const onChoose = () => {
        if (isFull) {
            setErrorMessage(`You can upload up to ${MAX_FILES} PDFs.`);
            return;
        }
        inputRef.current?.click();
    };

    const addFiles = useCallback((fileList) => {
        const incoming = Array.from(fileList || []);
        const pdfs = incoming.filter((f) => f.type === "application/pdf");

        setFiles((prev) => {
            const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
            const existing = new Set(prev.map(key));

            // de-dupe vs existing
            const deduped = pdfs.filter((f) => !existing.has(key(f)));

            const remaining = Math.max(0, MAX_FILES - prev.length);
            const accepted = deduped.slice(0, remaining);

            if (deduped.length > remaining) {
                const notAdded = deduped.length - accepted.length;
                setErrorMessage(
                    `You can upload up to ${MAX_FILES} PDFs. ${notAdded} file(s) not added.`
                );
            } else {
                setErrorMessage("");
            }

            return remaining ? [...prev, ...accepted] : prev;
        });
    }, []);

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
        },
        [addFiles]
    );

    const onInputChange = (e) => {
        addFiles(e.target.files);
        // allow selecting the same file(s) again later
        e.target.value = "";
    };

    const removeAt = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
        setErrorMessage(""); // clear any previous limit message
    };

    function submit() {
        if (!files.length) {
            return;
        }
        setLoading(true);
        setSubmitted(false);
        setError(false);
        setEmail(null);
        fetchMultiplePresignedUrls(files.length, true)
            .then(async (response) => {
                if (response && response.presignedUrls && response.presignedUrls.length > 0) {
                    const presignedUrls = response.presignedUrls;

                    const filesWithKeys = files.map((file, i) => ({
                        file,
                        key: presignedUrls[i].key,
                        url: presignedUrls[i].url
                    }));

                    const manuscripts = filesWithKeys.map(item => ({
                        name: item.file.name,
                        key: item.key
                    }));

                    // Upload all in parallel
                    const uploadPromises = filesWithKeys.map(item =>
                        fetch(item.url, {
                            method: "PUT",
                            headers: {"Content-Type": "application/pdf"},
                            body: item.file
                        }).then(resp => ({resp, key: item.key, name: item.file.name}))
                    );
                    const results = await Promise.all(uploadPromises);

                    // Fail check
                    if (results.some(r => !r.resp.ok)) {
                        console.error("Upload failed!");
                        setLoading(false);
                        setError(true);
                        return;
                    }

                    insertMultipleManuscripts(manuscripts, batchName, true)
                        .then(response => {
                            if (response.success && response.email != null) {
                                setFiles([])
                                setEmail(response.email)
                                setSubmitted(true);
                                setError(false);
                                setBatchName("");
                            } else {
                                setError(true);
                            }
                            setLoading(false);
                        })
                        .catch((e) => {
                            setError(true);
                            setLoading(false);
                        });
                } else {
                    console.error("0 presigned urls returned")
                    setLoading(false);
                    setError(true);
                }
            })
            .catch((e) => {
                console.error(e);
                setErrorMessage("Upload failed. Please try again.");
                setError(true);
            });
    }

    // const submitt = async () => {
    //     if (!files.length) return;
    //     setLoading(true);
    //     setSubmitted(false);
    //     setError(false);
    //     setEmail(null);
    //     try {
    //         const response = await uploadManuscriptsBatch(files, batchName, true);
    //         setFiles([]);
    //         setBatchName("");
    //         setErrorMessage("");
    //         setSubmitted(true);
    //         if (response && response.email != null) {  // ✅ check safely
    //             setEmail(response.email);
    //         }
    //     } catch (e) {
    //         console.error(e);
    //         setErrorMessage("Upload failed. Please try again.");
    //         setError(true);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    return (
        <div>
            <Header page="home"/>
            {loading ? (
                <Loading/>
            ) : (
                <div className="body">
                    {/*{errorMessage ? <AlertMessage message={errorMessage}/> : null}*/}
                    <div className="heading">Batch Upload Manuscripts</div>
                    {error ?
                        <div className={"score-submitted-text"} style={{border: '2px solid red'}}>There was an error
                            uploading your files. Please try again.
                        </div>
                        :
                        submitted ?
                            <div className={"score-submitted-text"} style={{border: '2px solid green'}}>Your manuscripts
                                have been submitted! We will send an
                                email to {email} when processing is complete. You
                                may check the status of all of your submissions on the submissions page.
                            </div>
                            :
                            null
                    }
                    <div className="upload-card">
                        <br/>
                        {/* Drag & drop area */}
                        <div
                            className={`dropzone ${dragOver ? "is-drag" : ""} ${
                                isFull ? "is-disabled" : ""
                            }`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={onChoose}
                            role="button"
                            tabIndex={0}
                            aria-disabled={isFull}
                            title={isFull ? `Maximum of ${MAX_FILES} files reached` : ""}
                        >
                            {/* simple upload icon */}
                            <svg width="40" height="40" viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M12 4l4 4-1.4 1.4L13 7.8V16h-2V7.8L9.4 9.4 8 8l4-4zM5 18h14v2H5z"
                                    fill="#666"
                                />
                            </svg>
                            <div className="drop-label">Drag and drop files here</div>
                            <div className="hint-text">Max {MAX_FILES} files</div>

                            <input
                                ref={inputRef}
                                type="file"
                                accept="application/pdf"
                                multiple
                                hidden
                                onChange={onInputChange}
                            />
                        </div>

                        <button
                            className="choose-btn"
                            type="button"
                            onClick={onChoose}
                            disabled={isFull}
                            title={isFull ? `Maximum of ${MAX_FILES} files reached` : ""}
                        >
                            Select PDF Files
                        </button>

                        {/* Batch name (optional) */}
                        <label className="field" style={{marginTop: 12}}>
                            <span>Batch Name <em>(optional)</em></span>
                            <input
                                type="text"
                                placeholder=""
                                value={batchName}
                                onChange={(e) => setBatchName(e.target.value)}
                            />
                        </label>

                        {/* File list */}
                        <ul className="file-list">
                            {files.map((f, i) => (
                                <li key={`${f.name}-${f.size}-${f.lastModified}`} className="file-row">
                                    <span className="file-icon" aria-hidden>📄</span>
                                    <span className="file-name">{f.name}</span>
                                    <button
                                        type="button"
                                        className="file-remove"
                                        aria-label={`Remove ${f.name}`}
                                        onClick={() => removeAt(i)}
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <button
                            className="upload-btn"
                            type="button"
                            disabled={!files.length}
                            onClick={() => submit()}
                        >
                            Upload ({files.length}/{MAX_FILES})
                        </button>
                    </div>
                    <button onClick={() => navigate("/")} className={"submit fade-in margin-top-1rem"}
                    >Home
                    </button>
                </div>
            )}
        </div>
    );
}

export default UploadBatch;
